import type {
  PlannerQueryResultItem,
  PlannerTemplateFragment,
} from "@/graphql";
import toRRuleState from "@/helpers/rrules/toRRuleState";
import { fromISODate, toDate, toUnix } from "@/stories/utils/dates";
import { nanoid } from "nanoid";
import * as Sentry from "@sentry/vue";
import type {
  DateRangeAsObject,
  PlannerFormItem,
  PlannerItem,
  PlannerSearchResult,
  PlannerSubtask,
  WhenFilterOption,
  WhenFilterState,
} from "@/types";
import {
  Approval_State,
  Container_Type,
  Plannertask_Assignee_Type,
  Profile_Type,
  type PlannerSubtaskInput,
  type PlannerTaskAssigneeInput,
  type PlannerTaskCreateInput,
  type PlannerTaskEditInput,
} from "@/graphql/generated-types";
import type { IFileState } from "@/interfaces";
import toRRuleInput from "@/helpers/rrules/toRRuleInput";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { MODULE_THEMES, PLANNER_TASK_EMPTY_CATEGORY } from "@/consts";
import { useDates } from "@/stories/utils/useDates";
import { globali18n } from "@/i18n";

const toImageOrPictogram = (images: IFileState[] | undefined) => {
  let image: PlannerTaskCreateInput["image"] = null;
  let pictogramId: string | null = null;
  if (images) {
    const [firstItem] = images.length ? images : [undefined];
    if (firstItem?.externalSource === "pictogram" && firstItem.id) {
      pictogramId = firstItem.id;
    } else if (firstItem) {
      image = {
        body: firstItem.base64,
        container: Container_Type.Planner,
        title: firstItem.name,
        url: firstItem.url,
      };
    }
  }

  return { image, pictogramId };
};

const toSubtaskInputs = (
  subtasks: PlannerSubtask[],
  { includeId }: { includeId: boolean },
): PlannerSubtaskInput[] =>
  subtasks.map((item, sortOrder) => {
    const { image, pictogramId } = toImageOrPictogram(
      item.image ? [item.image] : [],
    );
    return {
      ...(includeId && item.id ? { id: item.id } : {}),
      image,
      pictogramId,
      sortOrder,
      title: item.title.trim(),
    };
  });

const toPlannerTaskCreateInput = (
  form: PlannerFormItem,
): PlannerTaskCreateInput => {
  const { image, pictogramId } = toImageOrPictogram(form.images);
  let rset: PlannerTaskCreateInput["rset"] = undefined;

  if (form.rruleState) {
    rset = {
      excludedDates: [],
      rule: toRRuleInput(form.rruleState),
    };
  }

  // Assignees
  const { residents, departments, employees } = form;
  const assignees: PlannerTaskAssigneeInput[] = employees.map((id) => {
    return {
      id: id,
      type: Plannertask_Assignee_Type.Employee,
    };
  });

  if (residents.length > 0) {
    const [resident] = residents;
    assignees.push({ id: resident, type: Plannertask_Assignee_Type.Resident });
  }

  if (departments.length > 0) {
    const [department] = departments;
    assignees.push({
      id: department,
      type: Plannertask_Assignee_Type.Department,
    });
  }

  // New subtasks never carry a client-invented id to the server
  const subtasks = toSubtaskInputs(form.subtasks, { includeId: false });

  return {
    title: form.title,
    end: format(toDate(form.endDate), "yyyy-MM-dd'T'HH:mm:ssXXX"),
    start: format(toDate(form.startDate), "yyyy-MM-dd'T'HH:mm:ssXXX"),
    pictogramId,
    image,
    categoryId:
      form.category === PLANNER_TASK_EMPTY_CATEGORY ? null : form.category,
    description: form.description,
    rset,
    isRepeat: !!rset,
    approvalState: Approval_State.Unneeded,
    assignees,
    notificationSettings: form.notificationSettings,
    subtasks,
    visibleForResident: form.visibleForResident,
  };
};

const toPlannerTaskEditInput = (
  form: PlannerFormItem,
): PlannerTaskEditInput => {
  const { image, pictogramId } = toImageOrPictogram(form.images);
  let rset: PlannerTaskEditInput["rset"] = undefined;

  if (form.rruleState) {
    rset = {
      excludedDates: [],
      rule: toRRuleInput(form.rruleState),
    };
  }

  // Assignees
  const { residents, departments, employees } = form;
  const assignees: PlannerTaskAssigneeInput[] = employees.map((id) => {
    return {
      id: id,
      type: Plannertask_Assignee_Type.Employee,
    };
  });

  if (residents.length > 0) {
    const [resident] = residents;
    assignees.push({ id: resident, type: Plannertask_Assignee_Type.Resident });
  }

  if (departments.length > 0) {
    const [department] = departments;
    assignees.push({
      id: department,
      type: Plannertask_Assignee_Type.Department,
    });
  }

  // Only real server ids are sent back; new rows are treated as inserts
  const subtasks = toSubtaskInputs(form.subtasks, { includeId: true });

  return {
    title: form.title,
    end: format(toDate(form.endDate), "yyyy-MM-dd'T'HH:mm:ssXXX"),
    start: format(toDate(form.startDate), "yyyy-MM-dd'T'HH:mm:ssXXX"),
    pictogramId,
    image,
    categoryId:
      form.category === PLANNER_TASK_EMPTY_CATEGORY ? null : form.category,
    description: form.description,
    rset,
    isRepeat: !!rset,
    approvalState: Approval_State.Unneeded,
    assignees,
    notificationSettings: [...form.notificationSettings],
    subtasks,
    visibleForResident: form.visibleForResident,
  };
};

const fromTemplateToPlannerFormItem = (
  template: PlannerTemplateFragment,
): Partial<PlannerFormItem> => {
  const nextStartDate = new Date();

  if (template.start) {
    const [hours, minutes] = template.start.split(":");

    if (hours && minutes) {
      nextStartDate.setHours(
        Number.parseInt(hours),
        Number.parseInt(minutes),
        0,
        0,
      );
    }
  }

  const nextEndDate = new Date(nextStartDate);

  if (template.duration !== null && template.duration !== undefined) {
    nextEndDate.setMinutes(nextEndDate.getMinutes() + template.duration);
  } else {
    nextEndDate.setHours(nextEndDate.getHours() + 1);
  }

  const imageState = template.pictogram || template.image;

  return {
    type: "create",
    title: template.title,
    category: template.category?.id || PLANNER_TASK_EMPTY_CATEGORY,
    startDate: toUnix(nextStartDate),
    endDate: toUnix(nextEndDate),
    images: imageState
      ? [
          {
            id: imageState.id,
            name: imageState.description || imageState.id,
            originalName: imageState.description || "",
            base64: imageState.source,
            url: imageState.source,
            type: "image",
            action: "upload",
            ...(template.pictogram
              ? { externalSource: "pictogram" as const }
              : {}),
          },
        ]
      : [],
    imageType: template.pictogram ? "pictogram" : "upload",
    rruleState: template.repeat?.rset
      ? {
          ...toRRuleState(template.repeat.rset),
          startDate: toUnix(nextStartDate),
        }
      : undefined,
    subtasks: (template.subtasks || []).map((subtask) => {
      return {
        key: nanoid(),
        title: subtask.title,
        mode: "data",
      };
    }),
    validation: undefined,
  };
};

/**
 * Detects duplicate/blank server subtask ids so a bad API payload can't
 * silently render or save duplicate subtasks; reports and self-heals.
 */
const sanitizePlannerSubtasks = (
  subtasks: NonNullable<PlannerItem["subtasks"]>,
  plannerItemId: string,
): NonNullable<PlannerItem["subtasks"]> => {
  const seenIds = new Set<string>();
  const hasDuplicateOrBlankId = subtasks.some((subtask) => {
    if (!subtask.id || seenIds.has(subtask.id)) {
      return true;
    }
    seenIds.add(subtask.id);
    return false;
  });

  if (!hasDuplicateOrBlankId) {
    return subtasks;
  }

  Sentry.captureException(
    new Error(
      `Planner: duplicate or blank subtask id received for task ${plannerItemId}`,
    ),
  );

  const dedupedIds = new Set<string>();
  return subtasks.filter((subtask) => {
    if (!subtask.id || dedupedIds.has(subtask.id)) {
      return false;
    }
    dedupedIds.add(subtask.id);
    return true;
  });
};

const fromPlannerItemToPlannerFormItem = (
  item: PlannerItem,
): PlannerFormItem => {
  const startDate = toUnix(new Date(item.startDate));
  const endDate = toUnix(new Date(item.endDate));

  const imageState = item.pictogram || item.picture;
  const imageId =
    imageState && "id" in imageState && typeof imageState.id === "string"
      ? imageState.id
      : undefined;

  return {
    type: "edit",
    id: item.id,
    isRepeating: item.__typename === "RepeatingPlannerTask",
    title: item.title,
    category: item.category?.id || PLANNER_TASK_EMPTY_CATEGORY,
    startDate,
    endDate,
    description: item.description || "",
    employees: item.assignees
      .filter((assignee) => assignee.__typename === "Employee")
      .map((assignee) => assignee.id),
    departments: item.assignees
      .filter((assignee) => assignee.__typename === "Department")
      .map((assignee) => assignee.id),
    residents: item.assignees
      .filter((assignee) => assignee.__typename === "Resident")
      .map((assignee) => assignee.id),
    images: imageState
      ? [
          {
            id: imageId,
            name: imageId || item.title,
            originalName: imageId || "",
            base64: imageState.source,
            url: imageState.source,
            type: "image",
            action: "upload",
            ...(item.pictogram ? { externalSource: "pictogram" as const } : {}),
          },
        ]
      : [],
    imageType: item.pictogram ? "pictogram" : "upload",
    rruleState:
      item.__typename === "RepeatingPlannerTask" && item.repeat?.rset
        ? toRRuleState(item.repeat.rset)
        : undefined,
    notificationSettings: (item.notificationSettings || []).map((item) => {
      return {
        profile: {
          type:
            item.profile.__typename === "Employee"
              ? Profile_Type.Employee
              : Profile_Type.Resident,
          id: item.profile.id,
        },
        reminderFirst: item.reminderFirst,
        reminderFourth: item.reminderFourth,
        reminderSecond: item.reminderSecond,
        reminderThird: item.reminderThird,
        state: item.state,
        triggerWhenAssignmentChange: item.triggerWhenAssignmentChange,
        triggerWhenAssignmentComplete: item.triggerWhenAssignmentComplete,
        triggerWhenAssignmentOverdue: item.triggerWhenAssignmentOverdue,
        triggerWhenNewAssignment: item.triggerWhenNewAssignment,
      };
    }),
    subtasks: sanitizePlannerSubtasks(item.subtasks || [], item.id)
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((subtask) => {
        const subtaskImage = subtask.pictogram || subtask.picture;

        return {
          key: nanoid(),
          id: subtask.id,
          title: subtask.title,
          mode: "data",
          image: subtaskImage
            ? {
                id: subtaskImage.id,
                name: subtaskImage.id,
                originalName: subtaskImage.id,
                base64: subtaskImage.source,
                url: subtaskImage.source,
                type: "image",
                action: "upload",
                ...(subtask.pictogram
                  ? { externalSource: "pictogram" as const }
                  : {}),
              }
            : undefined,
        };
      }),
    validation: undefined,
    visibleForResident: item.visibleForResident || false,
  };
};

const fromQueryResultToSearchResult = (
  item: PlannerQueryResultItem,
): PlannerSearchResult => {
  const { formatRange } = useDates();
  const { t } = globali18n;

  if (
    item.__typename === "SinglePlannerTask" ||
    item.__typename === "RepeatingPlannerTask"
  ) {
    const categoryTitle = item.category?.title || t("planner.no_category");
    const start = toUnix(new Date(item.startDate));
    const end = toUnix(new Date(item.endDate));
    const range = formatRange(new Date(item.startDate), new Date(item.endDate));

    return {
      id: item.id,
      title: item.title,
      description: `${categoryTitle} - ${range}`,
      imageURL: item.picture?.source || item.pictogram?.source,
      categoryColor: item.category?.color || "#D1D1D1",
      categoryId: item.category?.id,
      categoryTitle,
      start,
      end,
      completed: item.completed,
      isRepeating: item.__typename === "RepeatingPlannerTask",
      residents: (item.assignees || [])
        .filter((item) => item.__typename === "Resident")
        .map((item) => item.id),
      employees: (item.assignees || [])
        .filter((item) => item.__typename === "Employee")
        .map((item) => item.id),
      departments: (item.assignees || [])
        .filter((item) => item.__typename === "Department")
        .map((item) => item.id),
    };
  }

  if (
    item.__typename === "SingleActivity" ||
    item.__typename === "RepeatingActivity"
  ) {
    const categoryTitle = t("global.activity");
    const start = toUnix(new Date(item.startDate));
    const end = toUnix(new Date(item.endDate));
    const range = formatRange(new Date(item.startDate), new Date(item.endDate));

    return {
      entityType: "activity",
      id: item.id,
      title: item.title,
      description: `${categoryTitle} - ${range}`,
      imageURL: item.image?.source || "",
      categoryColor: MODULE_THEMES.activities.color,
      categoryTitle,
      start,
      end,
      completed: false,
      isRepeating: item.__typename === "RepeatingActivity",
      residents: (item.participants || [])
        .filter((item) => item.__typename === "Resident")
        .map((item) => item.id),
      employees: (item.participants || [])
        .filter((item) => item.__typename === "Employee")
        .map((item) => item.id),
      departments: (item.departments || []).map((d) => d.id),
    };
  }

  if (item.__typename === "SingleMeal" || item.__typename === "RepeatingMeal") {
    const categoryTitle = t("global.meal");
    const start = toUnix(new Date(item.startDate));
    const end = toUnix(new Date(item.endDate));
    const range = formatRange(new Date(item.startDate), new Date(item.endDate));

    return {
      entityType: "meal",
      id: item.id,
      title: item.title,
      description: `${categoryTitle} - ${range}`,
      imageURL: item.coverImage?.source || "",
      categoryColor: MODULE_THEMES.meals.color,
      categoryTitle,
      start,
      end,
      completed: false,
      isRepeating: item.__typename === "RepeatingMeal",
      residents: (item.responsible || [])
        .filter((item) => item.__typename === "Resident")
        .map((item) => item.id),
      employees: (item.responsible || [])
        .filter((item) => item.__typename === "Employee")
        .map((item) => item.id),
      departments: (item.departments || []).map((d) => d.id),
    };
  }

  if (
    item.__typename === "SingleMeeting" ||
    item.__typename === "RepeatingMeeting"
  ) {
    const categoryTitle = t("global.meeting");
    const start = toUnix(new Date(item.startDate));
    const end = toUnix(new Date(item.endDate));
    const range = formatRange(new Date(item.startDate), new Date(item.endDate));

    return {
      entityType: "meeting",
      id: item.id,
      title: item.title,
      description: `${categoryTitle} - ${range}`,
      imageURL: item.coverImage?.source || "",
      categoryColor: MODULE_THEMES.meetings.color,
      categoryTitle,
      start,
      end,
      completed: false,
      isRepeating: item.__typename === "RepeatingMeeting",
      residents: (item.participants || [])
        .filter((item) => item.__typename === "Resident")
        .map((item) => item.id),
      employees: (item.participants || [])
        .filter((item) => item.__typename === "Employee")
        .map((item) => item.id),
      departments: (item.departments || []).map((d) => d.id),
    };
  }

  if (item.__typename === "BookingSlot") {
    const categoryTitle = t("global.booking");
    const start = toUnix(new Date(item.start));
    const end = toUnix(new Date(item.end));
    const range = formatRange(new Date(item.start), new Date(item.end));

    return {
      entityType: "booking",
      id: item.id,
      title: item.description || item.resource.title || "",
      description: `${categoryTitle} - ${range}`,
      imageURL: item.resource.coverImage?.source || "",
      categoryColor: MODULE_THEMES.booking.color,
      categoryTitle,
      start,
      end,
      isRepeating: false,
      completed: false,
      residents: (item.participants || [])
        .filter((item) => item.__typename === "Resident")
        .map((item) => item.id),
      employees: (item.participants || [])
        .filter((item) => item.__typename === "Employee")
        .map((item) => item.id),
      departments: [],
    };
  }

  throw new Error(`Unknown type: ${item.__typename}`);
};

const fromPlannerItemToSearchResult = (
  task: PlannerItem,
): Partial<PlannerSearchResult> => {
  const { formatRange } = useDates();
  const { t } = globali18n;

  const categoryTitle = task.category?.title || t("planner.no_category");
  const start = toUnix(new Date(task.startDate));
  const end = toUnix(new Date(task.endDate));
  const range = formatRange(new Date(task.startDate), new Date(task.endDate));

  return {
    categoryColor: task.category?.color,
    categoryId: task.category?.id,
    categoryTitle: categoryTitle,
    description: `${categoryTitle} - ${range}`,
    start: start,
    end: end,
    departments: task.assignees
      .filter((i) => i.__typename === "Department")
      .map((i) => i.id),
    employees: task.assignees
      .filter((i) => i.__typename === "Employee")
      .map((i) => i.id),
    residents: task.assignees
      .filter((i) => i.__typename === "Resident")
      .map((i) => i.id),
    imageURL: task.picture?.source || task.pictogram?.source,
    title: task.title,
    completed: task.completed,
  };
};

const fromWhenFilterStateToDateRange = (
  state: WhenFilterState,
): DateRangeAsObject => {
  const { option } = state;
  const end = endOfDay(new Date());

  if (option === "today") {
    return { start: startOfDay(new Date()), end };
  }

  const DAY_MAP: Partial<Record<WhenFilterOption, number>> = {
    last7: 7,
    last30: 30,
    last90: 90,
    last180: 180,
  };

  const days = DAY_MAP[option];
  if (days) {
    return { start: startOfDay(subDays(new Date(), days)), end };
  }

  if (state.customFrom && state.customTo) {
    return {
      start: startOfDay(fromISODate(state.customFrom)),
      end: endOfDay(fromISODate(state.customTo)),
    };
  }

  throw new Error(`Date range error: ${JSON.stringify(state)}`);
};

const fromSearchToSimpleItem = (item: PlannerSearchResult): PlannerItem => {
  const startDate = format(toDate(item.start), "yyyy-MM-dd'T'HH:mm:ssXXX");
  const endDate = format(toDate(item.end), "yyyy-MM-dd'T'HH:mm:ssXXX");

  const assignees = [
    ...item.employees.map((id) => ({ __typename: "Employee" as const, id })),
    ...item.residents.map((id) => ({ __typename: "Resident" as const, id })),
    ...item.departments.map((id) => ({
      __typename: "Department" as const,
      id,
    })),
  ];

  return {
    id: item.id,
    title: item.title,
    description: null,
    visibleForResident: false,
    completed: false,
    approvalState: Approval_State.Unneeded,
    startDate,
    endDate,
    assignees,
    picture: item.imageURL ? { source: item.imageURL } : null,
    pictogram: null,
    subtasks: null,
    notificationSettings: null,
    category: null,
    itemSource: "entity",
    entityType: item.entityType,
    __typename: "SinglePlannerTask",
  };
};

export default {
  toPlannerTaskCreateInput,
  toPlannerTaskEditInput,
  fromTemplateToPlannerFormItem,
  fromPlannerItemToPlannerFormItem,
  fromQueryResultToSearchResult,
  fromPlannerItemToSearchResult,
  fromWhenFilterStateToDateRange,
  fromSearchToSimpleItem,
};
